/**
 * 新增电站「基本信息」步骤的共享状态（模块级单例）。
 * 父页在其它步骤挂载时仍可校验、组装 payload、上传图片。
 *
 * 项目信息：名称、编码、简称、电站地址（省市区乡镇+详细）、投资方、业主方、渠道方、业务模式、国家。
 * 运营信息：行业类别、是否有光伏、是否有充电桩、电价类型、运营模式、用电类别、电压等级、电站类型、应用场景、计费模式。
 * 其他：显示优先级；地图经纬度与电站照片在步骤组件中维护。
 */
import { ref, reactive, computed, watch } from 'vue'
import dayjs from 'dayjs'
import { eui } from 'ems-ui'
import { uploadImage as uploadStationImage } from '@/api/device/station'
import * as customerApi from '@/api/device/customer'
import { regionData } from '@/utils/regionData'
import { DEVICE_FLAG_BITS, SELECTABLE_DEVICE_FLAG_BITS, buildDeviceFlag } from '@/utils/deviceFlag'

const mapSearchKeyword = ref('')

const INITIAL_FORM = {
  // 项目信息
  projectName: '',
  projectCode: '',
  projectShortName: '',
  maxpower: '',
  commossioningTime: '',
  country: '中国',
  businessMode: 'self_invest',
  area: '',
  province: '',
  city: '',
  district: '',
  township: '',
  address: '',
  investorEpc: '',
  ownerEnterprise: '',
  channelFramework: '',
  industryCategory: '',
  strategySwitch: true,
  /** 设备配置（多选，bit 位序号）：1 光伏、2 充电桩、4 变压器；储能 bit0 固定必选。
   *  bit5 按间隔统计电量默认不勾选，需按电站实际能力手动开启。 */
  deviceFlags: [1, 2, 4],
  operationModel: '',
  electricityCategory: '',
  voltage: '',
  stationType: '',
  applicationScenario: '',
  billingMode: '',
  capacity: '',
  stationState: 1,
  meterModel: 1,
  /** 自定义控制权限（strategyConfig）：防逆流 / 箱变容量变比 / 无功工作模式 */
  antiBackflow: false,
  transformerCapacityRatio: false,
  reactivePowerWorking: false,
  /** 自动需量：开启 0 / 关闭 -1 */
  autoDemand: false,
  /** 联系手机号：勾选任一自定义控制权限后必填 */
  contactPhone: '',
  // 其他
  sort: '',
  longitude: null,
  latitude: null,
}

/** 每次生成一份新的默认表单快照：建站日期取当天，deviceFlags 深拷贝避免共享引用。 */
function createDefaultForm() {
  return {
    ...INITIAL_FORM,
    commossioningTime: dayjs().format('YYYY-MM-DD'),
    deviceFlags: [...INITIAL_FORM.deviceFlags],
  }
}

const form = reactive(createDefaultForm())

function resetPhotoSlots() {
  const preview = photoSlot.value?.preview
  if (preview?.startsWith('blob:')) URL.revokeObjectURL(preview)
  photoSlot.value = {}
}

function resetForm() {
  Object.assign(form, createDefaultForm())
  mapSearchKeyword.value = ''
  resetPhotoSlots()
}

/** 设备配置（多选，bit 位序号）：
 * bit0 储能固定必选，bit1 光伏、bit2 充电桩、bit4 变压器、bit5 按间隔统计电量，其他位预留。
 * 后端 deviceFlag = 各位 OR 之和，例如储能 + 光伏 + 充电桩 = 0b111 = 7。
 * 注意 value 是 bit 序号而非掩码，例如变压器 value=4 对应掩码 16。
 */
const deviceFlagOptions = [
  { title: '光伏', value: DEVICE_FLAG_BITS.PV },
  { title: '充电桩', value: DEVICE_FLAG_BITS.CHARGE_PILE },
  { title: '变压器', value: DEVICE_FLAG_BITS.TRANSFORMER },
  { title: '按间隔统计电量', value: DEVICE_FLAG_BITS.INTERVAL_ENERGY },
]

/** 行业类别 */
const industryCategoryOptions = [
  { title: '制造业', value: '制造业' },
  { title: '电热气水生产与供应', value: '电热气水生产与供应' },
  { title: '批发与零售', value: '批发与零售' },
  { title: '交通运输与仓储物流', value: '交通运输与仓储物流' },
  { title: '信息与软件服务业', value: '信息与软件服务业' },
  { title: '房地产与园区', value: '房地产与园区' },
  { title: '公共与社会服务', value: '公共与社会服务' },
  { title: '其它', value: '其它' },
]

/** 需求：自运营模式、合同能源管理 */
const operationModelOptions = [
  { title: '自运营模式', value: 'self' },
  { title: '合同能源管理', value: 'emc' },
]

/** 需求：一般工业用电、大工业用电 */
const electricityCategoryOptions = [
  { title: '一般工业用电', value: 'industrial' },
  { title: '大工业用电', value: 'large_industrial' },
]

const businessModeOptions = [
  { title: '自投', value: 'self_invest' },
  { title: '非自投', value: 'non_self_invest' },
]

/** 地区（area）：与后端字典保持一致，直接以中文名作为值 */
const areaOptions = [
  { title: '中南', value: '中南' },
  { title: '华北', value: '华北' },
  { title: '西北', value: '西北' },
  { title: '华东', value: '华东' },
  { title: '华南', value: '华南' },
  { title: '华中', value: '华中' },
  { title: '西南', value: '西南' },
  { title: '东北', value: '东北' },
]

const CUSTOMER_TYPE_INVESTOR = '投资方'
const CUSTOMER_TYPE_PROPRIETOR = '业主方'
const CUSTOMER_TYPE_CHANNEL = '渠道方'
const CUSTOMER_PAGE_SIZE = 10
const CUSTOMER_SEARCH_DELAY = 250

const investorEpcOptions = ref([])
const proprietorSelectOptions = ref([])
const channelSelectOptions = ref([])
const investorEpcKeyword = ref('')
const ownerEnterpriseKeyword = ref('')
const channelFrameworkKeyword = ref('')
const investorEpcLoading = ref(false)
const ownerEnterpriseLoading = ref(false)
const channelFrameworkLoading = ref(false)
let customerOptionsLoadPromise = null
let customerOptionsInitialized = false

const customerSearchState = {
  investor: { timer: null, token: 0 },
  proprietor: { timer: null, token: 0 },
  channel: { timer: null, token: 0 },
}

function normalizeCustomerRow(item) {
  const title = String(item?.customerName ?? item?.searchValue ?? '').trim()
  if (!title) return null

  const subtitle = String(item?.shortName ?? '').trim()
  return {
    title,
    value: title,
    props: subtitle ? { subtitle } : undefined,
  }
}

function extractCustomerRows(res) {
  return Array.isArray(res?.rows) ? res.rows : []
}

function ensureSelectedCustomerOption(target, value) {
  const title = String(value || '').trim()
  if (!title) return
  if (target.value.some((item) => item.value === title)) return
  target.value = [{ title, value: title }, ...target.value]
}

function assignCustomerOptions(target, rows, selectedValue = '') {
  const list = rows.map(normalizeCustomerRow).filter(Boolean)
  const deduped = []
  const seen = new Set()

  list.forEach((item) => {
    if (seen.has(item.value)) return
    seen.add(item.value)
    deduped.push(item)
  })

  target.value = deduped
  ensureSelectedCustomerOption(target, selectedValue)
}

async function fetchCustomerOptionsByType(customerType, customerName = '') {
  const keyword = String(customerName || '').trim()
  const res = await customerApi.list({
    pageNum: 1,
    pageSize: CUSTOMER_PAGE_SIZE,
    customerType,
    customerName: keyword || undefined,
  })
  return extractCustomerRows(res)
}

async function queryCustomerOptions({ target, loading, customerType, keyword, selectedValue, state }) {
  const token = ++state.token
  loading.value = true

  try {
    const rows = await fetchCustomerOptionsByType(customerType, keyword)
    if (token !== state.token) return
    assignCustomerOptions(target, rows, selectedValue)
  } catch {
    if (token !== state.token) return
    ensureSelectedCustomerOption(target, selectedValue)
  } finally {
    if (token === state.token) {
      loading.value = false
    }
  }
}

function scheduleCustomerSearch({ keywordRef, target, loading, customerType, selectedValueGetter, state }) {
  watch(keywordRef, (keyword) => {
    if (state.timer) clearTimeout(state.timer)
    state.timer = setTimeout(() => {
      void queryCustomerOptions({
        target,
        loading,
        customerType,
        keyword,
        selectedValue: selectedValueGetter(),
        state,
      })
    }, CUSTOMER_SEARCH_DELAY)
  })
}

scheduleCustomerSearch({
  keywordRef: investorEpcKeyword,
  target: investorEpcOptions,
  loading: investorEpcLoading,
  customerType: CUSTOMER_TYPE_INVESTOR,
  selectedValueGetter: () => form.investorEpc,
  state: customerSearchState.investor,
})

scheduleCustomerSearch({
  keywordRef: ownerEnterpriseKeyword,
  target: proprietorSelectOptions,
  loading: ownerEnterpriseLoading,
  customerType: CUSTOMER_TYPE_PROPRIETOR,
  selectedValueGetter: () => form.ownerEnterprise,
  state: customerSearchState.proprietor,
})

scheduleCustomerSearch({
  keywordRef: channelFrameworkKeyword,
  target: channelSelectOptions,
  loading: channelFrameworkLoading,
  customerType: CUSTOMER_TYPE_CHANNEL,
  selectedValueGetter: () => form.channelFramework,
  state: customerSearchState.channel,
})

watch(() => form.investorEpc, (value) => {
  ensureSelectedCustomerOption(investorEpcOptions, value)
}, { immediate: true })

watch(() => form.ownerEnterprise, (value) => {
  ensureSelectedCustomerOption(proprietorSelectOptions, value)
}, { immediate: true })

watch(() => form.channelFramework, (value) => {
  ensureSelectedCustomerOption(channelSelectOptions, value)
}, { immediate: true })

/** 客户类型 → 对应的选项 ref 与表单字段名，供新增客户后刷新/回填使用 */
const customerTypeBindings = {
  [CUSTOMER_TYPE_INVESTOR]: { target: investorEpcOptions, field: 'investorEpc' },
  [CUSTOMER_TYPE_PROPRIETOR]: { target: proprietorSelectOptions, field: 'ownerEnterprise' },
  [CUSTOMER_TYPE_CHANNEL]: { target: channelSelectOptions, field: 'channelFramework' },
}

/** 新增客户成功后：重新拉取该类型下拉数据 */
async function refreshCustomerOptionsByType(customerType) {
  const binding = customerTypeBindings[customerType]
  if (!binding) return
  const rows = await fetchCustomerOptionsByType(customerType)
  assignCustomerOptions(binding.target, rows, form[binding.field])
}

/** 新增客户成功后：将新客户补入下拉并选中到对应字段 */
function applyCreatedCustomer(customerType, name) {
  const binding = customerTypeBindings[customerType]
  const title = String(name || '').trim()
  if (!binding || !title) return
  ensureSelectedCustomerOption(binding.target, title)
  form[binding.field] = title
}

async function ensureCustomerOptions() {
  if (customerOptionsInitialized) return

  if (!customerOptionsLoadPromise) {
    customerOptionsLoadPromise = Promise.all([
      fetchCustomerOptionsByType(CUSTOMER_TYPE_INVESTOR),
      fetchCustomerOptionsByType(CUSTOMER_TYPE_PROPRIETOR),
      fetchCustomerOptionsByType(CUSTOMER_TYPE_CHANNEL),
    ])
      .then(([investors, proprietors, channels]) => {
        assignCustomerOptions(investorEpcOptions, investors, form.investorEpc)
        assignCustomerOptions(proprietorSelectOptions, proprietors, form.ownerEnterprise)
        assignCustomerOptions(channelSelectOptions, channels, form.channelFramework)
        customerOptionsInitialized = true
        ensureOptionConsistency()
      })
      .catch((error) => {
        customerOptionsLoadPromise = null
        eui.showToast('获取客户下拉数据失败，请稍后重试', 'error')
        throw error
      })
  }

  return customerOptionsLoadPromise
}

const voltageLevelOptions = [
  { title: '10', value: '10' },
  { title: '20', value: '20' },
  { title: '35', value: '35' },
  { title: '110', value: '110' },
  { title: '220', value: '220' },
]

const meterModelOptions = [
  { title: '虚拟', value: 0 },
  { title: '实体', value: 1 },
]

const stationTypeOptions = [
  { title: '分布式', value: 'distributed' },
  { title: '集中式', value: 'centralized' },
]

const applicationScenarioOptions = [
  { title: '工商业', value: 'industrial_commercial' },
  { title: '源网侧', value: 'grid_side' },
  { title: '光储', value: 'pv_storage' },
]

const billingModeOptions = [
  { title: '容量计费', value: 'capacity' },
  { title: '需量计费', value: 'demand' },
  { title: '未知', value: 'unknown' },
]

const photoSlot = ref({})
const hiddenFileInput = ref(null)

/** 省 / 市 / 区三级树（/data/city.json），加载失败时省、市回退 regionData */
const cityTreeRaw = ref([])
let cityTreeLoadPromise = null

async function ensureCityTree() {
  if (cityTreeRaw.value.length) return cityTreeRaw.value
  if (!cityTreeLoadPromise) {
    cityTreeLoadPromise = fetch('/data/city.json')
      .then((r) => r.json())
      .then((list) => {
        cityTreeRaw.value = Array.isArray(list) ? list : []
        syncRegionCascadeFromForm()
        return cityTreeRaw.value
      })
      .catch(() => {
        cityTreeRaw.value = []
        return []
      })
  }
  return cityTreeLoadPromise
}

const provinceOptions = computed(() => {
  if (cityTreeRaw.value.length) {
    return cityTreeRaw.value.map((p) => p.value).filter(Boolean)
  }
  return Object.keys(regionData)
})

const cityOptions = computed(() => {
  if (!form.province) return []
  if (cityTreeRaw.value.length) {
    const prov = cityTreeRaw.value.find((p) => p.value === form.province)
    return (prov?.children || []).map((c) => c.value).filter(Boolean)
  }
  return regionData[form.province] || []
})

const districtOptions = computed(() => {
  if (!form.province || !form.city || !cityTreeRaw.value.length) return []
  const prov = cityTreeRaw.value.find((p) => p.value === form.province)
  const city = (prov?.children || []).find((c) => c.value === form.city)
  return (city?.children || []).map((d) => d.value).filter(Boolean)
})

function onProvinceChange() {
  form.city = ''
  form.district = ''
}

function onCityChange() {
  form.district = ''
}

/** 编辑回填时校验省市区是否仍在树内 */
function syncRegionCascadeFromForm() {
  if (!cityTreeRaw.value.length) return
  const prov = cityTreeRaw.value.find((p) => p.value === form.province)
  if (!prov) {
    form.province = ''
    form.city = ''
    form.district = ''
    return
  }
  const city = (prov.children || []).find((c) => c.value === form.city)
  if (!city) {
    form.city = ''
    form.district = ''
    return
  }
  const districtOk = (city.children || []).some((d) => d.value === form.district)
  if (form.district && !districtOk) form.district = ''
}

function validate() {
  // 按「基本信息」卡片自上而下的字段顺序校验，提示统一为「请填写 xxx」
  if (!form.commossioningTime) {
    eui.showToast('请选择建站日期', 'error')
    return false
  }
  if (!form.projectCode?.trim()) {
    eui.showToast('请填写项目编码', 'error')
    return false
  }
  if (!form.area?.trim()) {
    eui.showToast('请选择地区', 'error')
    return false
  }
  if (!form.province?.trim()) {
    eui.showToast('请选择省', 'error')
    return false
  }
  if (!form.investorEpc?.trim()) {
    eui.showToast('请选择投资方 (EPC)', 'error')
    return false
  }
  if (!form.ownerEnterprise?.trim()) {
    eui.showToast('请选择业主方 (安装企业)', 'error')
    return false
  }
  if (form.meterModel === '' || form.meterModel == null) {
    eui.showToast('请选择电表模式', 'error')
    return false
  }
  // 联系手机号：勾选任一自定义控制权限后必填；填了就要合法
  const contactPhone = form.contactPhone?.trim() || ''
  const hasCustomControlPermissions =
    !!form.antiBackflow || !!form.transformerCapacityRatio || !!form.reactivePowerWorking
  if (hasCustomControlPermissions && !contactPhone) {
    eui.showToast('勾选了自定义控制权限必须输入手机号', 'error')
    return false
  }
  if (contactPhone && !/^1[3-9]\d{9}$/.test(contactPhone)) {
    eui.showToast('请输入正确的手机号码格式', 'error')
    return false
  }
  return true
}

function numOrUndef(v) {
  if (v === '' || v == null) return undefined
  const n = Number(v)
  return Number.isFinite(n) ? n : undefined
}

/** 1 自运营 / 2 合同能源管理 */
const OPERATION_MODEL_CODE = {
  self: 1,
  emc: 2,
}

/** 1 自投 / 2 非自投 */
const BUSINESS_MODE_CODE = {
  self_invest: 1,
  non_self_invest: 2,
}

/** 0 大工业用电 / 1 一般工商业用电 */
const ELECTRICITY_CATEGORY_CODE = {
  large_industrial: 0,
  industrial: 1,
}

/** 1 分布式 / 2 集中式 */
const STATION_TYPE_CODE = {
  distributed: 1,
  centralized: 2,
}

/** 0 未知 / 1 容量计费 / 2 需量计费 */
const BILLING_MODE_CODE = {
  unknown: 0,
  capacity: 1,
  demand: 2,
}

function normalizeAddressDetail(fullAddress, regions = {}) {
  const full = String(fullAddress || '').trim()
  if (!full) return ''

  const regionLine = [regions.province, regions.city, regions.district, regions.township]
    .map((s) => String(s || '').trim())
    .filter(Boolean)
    .join('')

  if (regionLine && full.startsWith(regionLine)) {
    return full.slice(regionLine.length).trim()
  }
  return full
}

function buildBasicPayload() {
  const regionLine = [form.province, form.city, form.district, form.township]
    .map((s) => (s == null ? '' : String(s).trim()))
    .filter(Boolean)
    .join('')
  const street = normalizeAddressDetail(form.address)?.trim() ?? ''
  const composedAddress = `${regionLine}${street}`.trim()

  const sort = numOrUndef(form.sort)
  const classification = ELECTRICITY_CATEGORY_CODE[form.electricityCategory] ?? 1
  const energyStorageType = STATION_TYPE_CODE[form.stationType] ?? 1
  const billingMode = BILLING_MODE_CODE[form.billingMode] ?? 2
  const maxpower = numOrUndef(form.maxpower)
  const capacity = numOrUndef(form.capacity)
  const meterModel = Number(form.meterModel)
  const longitude = form.longitude == null || form.longitude === '' ? '' : String(form.longitude)
  const latitude = form.latitude == null || form.latitude === '' ? '' : String(form.latitude)

  // 设备配置：按 bit 位组装 deviceFlag（bit0 储能固定为 1）
  const deviceFlag = buildDeviceFlag(form.deviceFlags)

  const station = {
    name: form.projectName?.trim() || form.projectShortName?.trim() || form.projectCode?.trim(),
    code: form.projectCode?.trim(),
    uuid: form.projectCode?.trim(),
    shortName: form.projectShortName?.trim(),
    stationAbbreviation: form.projectShortName?.trim() || '',
    address: composedAddress || '',
    area: form.area?.trim() || '',
    province: form.province?.trim() || '',
    city: form.city?.trim() || '',
    district: form.district?.trim() || '',
    township: form.township?.trim() || '',
    country: form.country?.trim() || '',
    countryName: form.country?.trim() || '',
    commossioningTime: form.commossioningTime || '',
    longitude,
    latitude,
    customer: form.investorEpc?.trim() || '',
    operatingUnit: form.ownerEnterprise?.trim() || '',
    devopsUnit: form.channelFramework?.trim() || '',
    industryCategory: form.industryCategory || '',
    strategySwitch: !!form.strategySwitch,
    deviceFlag,
    businessModel: BUSINESS_MODE_CODE[form.businessMode] ?? 1,
    operatingModel: OPERATION_MODEL_CODE[form.operationModel] ?? 1,
    classification,
    voltage: form.voltage || '',
    energyStorageType,
    billingMode,
    maxPower: maxpower,
    capacity,
    stationState: Number(form.stationState),
    meterModel: Number.isInteger(meterModel) ? meterModel : undefined, sort: sort ?? 0,
  }

  const strategyConfig = {
    phoneNumber: form.contactPhone?.trim() || '',
    preventCounterCurrent: form.antiBackflow ? 1 : 0,
    transformerCapacityRatio: form.transformerCapacityRatio ? 1 : 0,
    reactivePowerWorking: form.reactivePowerWorking ? 1 : 0,
    autoDemand: form.autoDemand ? 0 : -1,
  }

  return { station, strategyConfig }
}

function getPhotoUrl() {
  return photoSlot.value?.url || ''
}

function setPhotoSlotFromStationPicture(stationPicture) {
  resetPhotoSlots()
  const picture = String(stationPicture || '')
    .split(',')
    .map((item) => item.trim())
    .find(Boolean)

  if (picture) {
    photoSlot.value = { preview: picture, url: picture }
  }
}

function triggerPick() {
  hiddenFileInput.value?.click?.()
}

function normalizeUploadedPicture(res) {
  const payload = res?.data ?? res
  if (typeof payload === 'string') return payload
  if (!payload || typeof payload !== 'object') return ''
  return payload.data || ''
}

async function onHiddenFileChange(e) {
  const file = e.target.files?.[0]
  e.target.value = ''
  if (!file) return
  if (!file.type?.startsWith?.('image/')) {
    eui.showToast('请上传图片文件', 'error')
    return
  }
  if (file.size > 5 * 1024 * 1024) {
    eui.showToast('单张图片不能超过 5MB', 'error')
    return
  }
  const fd = new FormData()
  fd.append('stationPicture', file, file.name)
  fd.append('province', form.province?.trim() || '')

  try {
    const res = await uploadStationImage(fd)
    const picture = normalizeUploadedPicture(res)
    if (!picture) {
      eui.showToast('图片上传失败，请重试', 'error')
      return
    }

    const prev = photoSlot.value?.preview
    if (prev?.startsWith('blob:')) URL.revokeObjectURL(prev)
    photoSlot.value = { file, preview: picture, url: picture }
  } catch (err) {
    eui.showToast('图片上传失败，请重试', 'error')
  }
}

function removePhotoSlot() {
  resetPhotoSlots()
}

function ensureOptionConsistency() {
  form.strategySwitch = !!form.strategySwitch
  if (!Array.isArray(form.deviceFlags)) {
    form.deviceFlags = [...INITIAL_FORM.deviceFlags]
  } else {
    form.deviceFlags = Array.from(new Set(form.deviceFlags.map((v) => Number(v))))
      .filter((v) => Number.isInteger(v) && SELECTABLE_DEVICE_FLAG_BITS.includes(v))
      .sort((a, b) => a - b)
  }
  if (!operationModelOptions.some((o) => o.value === form.operationModel)) {
    form.operationModel = operationModelOptions[0].value
  }
  if (!electricityCategoryOptions.some((o) => o.value === form.electricityCategory)) {
    form.electricityCategory = electricityCategoryOptions[0].value
  }
  if (!businessModeOptions.some((o) => o.value === form.businessMode)) {
    form.businessMode = businessModeOptions[0].value
  }
  if (!voltageLevelOptions.some((o) => o.value === form.voltage)) {
    form.voltage = voltageLevelOptions[0].value
  }
  if (!stationTypeOptions.some((o) => o.value === form.stationType)) {
    form.stationType = stationTypeOptions[0].value
  }
  if (!applicationScenarioOptions.some((o) => o.value === form.applicationScenario)) {
    form.applicationScenario = applicationScenarioOptions[0].value
  }
  if (!billingModeOptions.some((o) => o.value === form.billingMode)) {
    form.billingMode = billingModeOptions[0].value
  }
  form.stationState = Number(form.stationState)
  if (!meterModelOptions.some((o) => o.value === Number(form.meterModel))) {
    form.meterModel = ''
  } else {
    form.meterModel = Number(form.meterModel)
  }
  if (form.investorEpc && !investorEpcOptions.value.some((o) => o.value === form.investorEpc)) {
    ensureSelectedCustomerOption(investorEpcOptions, form.investorEpc)
  }
  if (form.ownerEnterprise && !proprietorSelectOptions.value.some((o) => o.value === form.ownerEnterprise)) {
    ensureSelectedCustomerOption(proprietorSelectOptions, form.ownerEnterprise)
  }
  if (form.channelFramework && !channelSelectOptions.value.some((o) => o.value === form.channelFramework)) {
    ensureSelectedCustomerOption(channelSelectOptions, form.channelFramework)
  }
}

export function useStationAddBasic() {
  ensureOptionConsistency()
  ensureCityTree()
  void ensureCustomerOptions().catch(() => { })
  return {
    form,
    mapSearchKeyword,
    provinceOptions,
    cityOptions,
    districtOptions,
    onProvinceChange,
    onCityChange,
    ensureCityTree,
    syncRegionCascadeFromForm,
    operationModelOptions,
    industryCategoryOptions,
    deviceFlagOptions,
    electricityCategoryOptions,
    businessModeOptions,
    areaOptions,
    investorEpcOptions,
    proprietorSelectOptions,
    channelSelectOptions,
    investorEpcKeyword,
    ownerEnterpriseKeyword,
    channelFrameworkKeyword,
    investorEpcLoading,
    ownerEnterpriseLoading,
    channelFrameworkLoading,
    voltageLevelOptions,
    meterModelOptions,
    stationTypeOptions,
    applicationScenarioOptions,
    applicationScenarioOptions,
    billingModeOptions,
    photoSlot,
    hiddenFileInput,
    validate,
    buildBasicPayload,
    resetForm,
    setPhotoSlotFromStationPicture,
    getPhotoUrl,
    triggerPick,
    onHiddenFileChange,
    removePhotoSlot,
    normalizeAddressDetail,
    ensureCustomerOptions,
    refreshCustomerOptionsByType,
    applyCreatedCustomer,
  }
}
